import {CommonModule, DecimalPipe} from '@angular/common';
import {Component, EventEmitter, Input, Output, OnDestroy, OnInit, DestroyRef, inject} from '@angular/core';
import {DxButtonComponent, DxDateRangeBoxComponent, DxSelectBoxComponent} from 'devextreme-angular';
import {ChartDataPoint, DoughnutChartComponent} from "../doughnut-chart/doughnut-chart.component";
import {TrendingComponent} from "../trending/trending.component";
import {AggregateType, ExportedFileDto, IMyScoreShiftQuery, ITrendingQuery, ScoreDto, ShiftType, TrendingDto} from "@services";
import EnumUtils from "../../../shared/enum-utils";
import {FileUtils} from "../../../shared/file-utils";
import {CapitalCasePipe} from "../../../pipes/capital-case.pipe";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Observable} from "rxjs";

@Component({
    selector: 'app-score-dashboard',
    imports: [CommonModule, DoughnutChartComponent, DxDateRangeBoxComponent, DxButtonComponent, DxSelectBoxComponent, TrendingComponent, CapitalCasePipe, DecimalPipe,],
    templateUrl: './score-dashboard.component.html',
    styles: [`
        :host { display: block; height: 100%; overflow: auto; }
    `]
})
export class ScoreDashboardComponent implements OnInit, OnDestroy {
    private destroyRef = inject(DestroyRef);

    @Input() title: string = 'Score';
    @Input() trendingTitle: string = 'Trending';
    @Input() centerText: string = 'My Score';
    @Input() employeeId: string | null;
    @Input() daysLookBack: number = 0;
    @Input() palette: Record<string, string> = {};
    @Input() noDataExclude: string[] = [];
    @Input() scoreCountsAsData = false;
    @Input() showBreakdownCharts: boolean = true;
    @Input() useCapitalCaseFormatting: boolean = true;
    @Input() containerClass: string = 'p-4 h-100 overflow-auto';

    @Input() showScore: boolean = true;
    @Input() showTrending: boolean = true;
    @Input() showFinancials: boolean = false;
    @Input() formatMode: 'percent' | 'decimal' = 'percent';

    @Input() loadScoreData: (query: IMyScoreShiftQuery) => Observable<ScoreDto>;
    @Input() loadTrendingData: (query: ITrendingQuery) => Observable<TrendingDto>;
    @Input() exportData: (from: Date, to: Date, userId: string | null) => Observable<ExportedFileDto>;
    @Input() processScoreData: (items: ChartDataPoint[]) => ChartDataPoint[];

    @Output() dateRangeChanged = new EventEmitter<[Date, Date]>();
    @Output() dataLoaded = new EventEmitter<ChartDataPoint[]>();

    dateRangeFilterValue: [Date, Date] = [
        new Date(new Date().setHours(0, 0, 0, 0)),
        new Date(new Date().setHours(0, 0, 0, 0)),
    ];
    trendingFilterOptions = EnumUtils.getEnumOptions(AggregateType);
    trendingFilterValue: AggregateType = AggregateType.Day;
    scoreData: ChartDataPoint[] = [];
    readonly ShiftType = ShiftType;

    private chartLoaded: { [key in ShiftType]: boolean } = {
        [ShiftType.Day]: false,
        [ShiftType.Afternoons]: false,
        [ShiftType.WeekendDays]: false,
        [ShiftType.WeekendNights]: false
    };

    ngOnInit(): void {
        this.initDateRange();
    }

    ngOnDestroy(): void {
    }

    get fromDate(): Date {
        return this.dateRangeFilterValue?.[0];
    }

    get toDate(): Date {
        return this.dateRangeFilterValue?.[1];
    }

    onDateRangeChanged(e: any) {
        this.dateRangeChanged.emit(this.dateRangeFilterValue);
    }

    onExport() {
        if (this.exportData) {
            this.exportData(this.fromDate, this.toDate, this.employeeId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((res) => FileUtils.downloadFile(res));
        }
    }

    onMainChartLoaded(items: ChartDataPoint[]) {
        this.scoreData = this.processScoreData ? this.processScoreData(items) : items;
        this.dataLoaded.emit(items);
    }

    showChart(shiftType: ShiftType): boolean {
        return this.chartLoaded[shiftType];
    }

    onShiftChartLoaded(items: ChartDataPoint[], shiftType: ShiftType) {
        if (items.length > 0) {
            this.chartLoaded[shiftType] = true;
        }
    }

    private initDateRange(): void {
        if (this.daysLookBack > 0) {
            this.dateRangeFilterValue = [new Date(Date.now() - this.daysLookBack * 24 * 60 * 60 * 1000), new Date()];
            this.dateRangeChanged.emit(this.dateRangeFilterValue);
        }
    }
}
